// Enum + rate source of truth for Post a Project.
// Values mirror app/models/job_postings/job_postings/constants.rb — keep in sync.
//
// These strings are submitted verbatim through Api::V1::ProjectPostingsController
// (project_posting[...] / *_posting_attributes) and value-validated server-side
// (step_validations.rb value_in_array, ActiveRecord enums). A wrong value 422s
// (or raises ArgumentError on an enum assignment). When a value here disagrees
// with CONFIG-SCHEMA's example, this file is right — it was read from the backend.
//
// Sourcing notes per block are inline. Verified 2026-06-25 against:
//   app/models/job_postings/job_postings/constants.rb
//   app/models/illustration_order.rb (ILLUSTRATION_SIZE_VALUES)
//   app/models/job_postings/narration_posting.rb (accent/gender enums)
//   app/models/job_postings/formatting_posting.rb (formatting_type enum)
//   app/models/managed_service.rb + job_posting.rb (managed_services_tier enum)
//   config/locales/en.yml (managed services questionnaire copy)
//   app/views/job_postings/_ai_consent.html.erb (consent bullets)
//   app/views/job_postings/_trigger_warning_fields.html.erb (trigger categories)
//   app/views/job_postings/steps/cover_design/_six.erb (style slider labels)

// ---------------------------------------------------------------------------
// Bundles — constants.rb BUNDLE_TYPES / *_BUNDLE. bundle_tier submits the full
// string (CORRECTIONS: Silver / Gold / Platinum / Ultimate Fiction /
// Audiobook Narration). `key` is a client-side slug only (never submitted).
// ---------------------------------------------------------------------------
export const BUNDLE_TIERS = [
  { key: "silver", tier: "Silver" },
  { key: "gold", tier: "Gold" },
  { key: "platinum", tier: "Platinum" },
  { key: "ultimate_fiction", tier: "Ultimate Fiction" },
  { key: "audiobook_narration", tier: "Audiobook Narration" },
];

// ---------------------------------------------------------------------------
// Writing tiers — constants.rb WRITING_TIERS (book_writing). Order mirrors the
// constant: Finance and Technology is SECOND, not last (CONFIG-SCHEMA example
// had it last). ALL_TIER_ACCESS / FINANCE_AND_TECH_WRITING_TIER spelling exact.
// ---------------------------------------------------------------------------
export const ALL_TIER_VALUE = "Allow All to Request"; // constants.rb ALL_TIER_ACCESS

export const WRITING_TIERS = [
  "Allow All to Request", // ALL_TIER_ACCESS
  "Finance and Technology", // FINANCE_AND_TECH_WRITING_TIER
  "Premium",
  "Top",
  "Urban",
  "Rising",
];

// constants.rb FICTION_WRITING_TIERS (sample, humanize_ai) — no Finance & Tech
export const FICTION_WRITING_TIERS = [
  "Allow All to Request",
  "Premium",
  "Top",
  "Urban",
  "Rising",
];

// ---------------------------------------------------------------------------
// Genres / styles / niches — constants.rb
// ---------------------------------------------------------------------------

// constants.rb WRITING_GENRES (20) — book_writing/sample/humanize_ai/outline plot
export const WRITING_GENRES = [
  "Adventure",
  "Children's Stories (No Rhyming)",
  "Contemporary Romance",
  "Crime",
  "Erotica",
  "Fantasy",
  "Horror",
  "Mystery/Thriller",
  "Romance",
  "Science Fiction",
  "Western",
  "Other (Fiction)",
  "Contemporary",
  "Dystopian",
  "Historical Fiction",
  "LGBTQ Plus",
  "Paranormal",
  "Paranormal Romance",
  "Teens",
  "Urban Fantasy",
];

// constants.rb WRITING_STYLES (book_writing writing_style)
export const WRITING_STYLES = ["First Person", "Third Person"];

// constants.rb WRITING_NICHES (14) — "Other" last in the constant. Order is not
// server-validated (membership only); book_writing filters these by tier
// (niches_by_tier / NICHE_MAP) at the config layer.
export const WRITING_NICHES = [
  "Crafts, Hobbies, & Home",
  "Cookbooks, Food & Wine",
  "Engineering & Transportation",
  "Health & Fitness",
  "History",
  "Humor & Entertainment",
  "Parenting & Relationships",
  "Politics & Social Sciences",
  "Reference",
  "Religion & Spirituality",
  "Self-Help",
  "Sports & Outdoors",
  "Travel",
  "Other",
];

// constants.rb SEO_NICHES = WRITING_NICHES + "Business & Money"
export const SEO_NICHES = [...WRITING_NICHES, "Business & Money"];

// constants.rb ALL_ORDER_NICHES = WRITING_NICHES + [Business & Money,
// Computers & Technology]. cover_design/illustration views render this `.sort`ed,
// so we pre-sort to match the live dropdown order.
export const ALL_ORDER_NICHES = [
  "Crafts, Hobbies, & Home",
  "Cookbooks, Food & Wine",
  "Engineering & Transportation",
  "Health & Fitness",
  "History",
  "Humor & Entertainment",
  "Parenting & Relationships",
  "Politics & Social Sciences",
  "Reference",
  "Religion & Spirituality",
  "Self-Help",
  "Sports & Outdoors",
  "Travel",
  "Other",
  "Business & Money",
  "Computers & Technology",
].sort();

// Illustration and cover ask one "Niche / genre" question that covers both
// halves of the catalogue (AU-25 / UPDA-66 — Marco ruled 2026-08-10 that
// fiction IS supported for both). Grouped rather than a fiction toggle: neither
// posting has a `fiction` column and no controller permits one, so a toggle
// would be discarded on submit and resume as false, while the chosen niche
// persists on its own. constants.rb holds the matching server-side union
// (COVER_NICHES_AND_GENRES / ILLUSTRATION_NICHES_AND_GENRES).
export const NICHE_AND_GENRE_GROUPS = [
  { group: "Non-fiction", options: ALL_ORDER_NICHES.map((v) => ({ value: v, label: v })) },
  { group: "Fiction", options: WRITING_GENRES.map((v) => ({ value: v, label: v })) },
];

// Tier-scoped niche lists — constants.rb FINANCE_AND_TECHNOLOGY_NICHES /
// URBAN_OR_RISING_NICHES (constants.rb:66-68). Derived in JS so they stay exact
// and DRY off WRITING_NICHES. JS default `.sort()` is code-unit order, matching
// Ruby `Array#sort` for these ASCII strings.
const FINANCE_AND_TECHNOLOGY_NICHES = ["Business & Money", "Computers & Technology"];
const URBAN_OR_RISING_NICHES = WRITING_NICHES.filter(
  (n) => n !== "Cookbooks, Food & Wine",
);

// constants.rb NICHE_MAP (constants.rb:76-81) — raw tier→niches map. Note the
// 'Other' fallback key maps to WRITING_NICHES UNSORTED, exactly as the constant.
// (Used by sample/_one.html.erb data-niches + the request-posting modal.)
export const NICHE_MAP = {
  "Finance and Technology": [...FINANCE_AND_TECHNOLOGY_NICHES].sort(),
  Urban: [...URBAN_OR_RISING_NICHES].sort(),
  Rising: [...URBAN_OR_RISING_NICHES].sort(),
  Other: WRITING_NICHES,
};

// Tier-scoped niche option list for book_writing / humanize_ai / sample.
// Mirrors helper job_postings_helper.rb#niches_by_tier (lines 167-177): Finance
// and Technology → its 2-niche set; Urban/Rising → their reduced set (no
// Cookbooks); everything else (Premium, Top, Allow All to Request, null) → the
// full WRITING_NICHES. All branches return a sorted copy, matching the helper.
export function nichesForTier(tier) {
  if (tier === "Finance and Technology") {
    return [...FINANCE_AND_TECHNOLOGY_NICHES].sort();
  }

  if (tier === "Urban" || tier === "Rising") {
    return [...URBAN_OR_RISING_NICHES].sort();
  }

  return [...WRITING_NICHES].sort();
}

// ---------------------------------------------------------------------------
// Illustration — illustration_order.rb ILLUSTRATION_SIZE_VALUES (15, quote
// formatted, used by both the view and step_validations for `page_size`).
// constants.rb for interior/type. NOTE: `illustration_type` is validated against
// constants.rb ILLUSTRATION_TYPES ("counts as two") — NOT illustration_order.rb's
// ILLUSTRATION_TYPE_VALUES ("counts as 2"). Use the "two" spelling.
// ---------------------------------------------------------------------------
export const ILLUSTRATION_SIZES = [
  '5" X 8"',
  '5.06" X 7.81"',
  '5.25" X 8"',
  '5.5" X 8.5"',
  '6" X 9"',
  '6.14" X 9.21"',
  '6.69" X 9.61"',
  '7" X 10"',
  '7.44" X 9.69"',
  '7.5" X 9.25"',
  '8" X 10"',
  '8.5" X 11"',
  '8.25" X 6"',
  '8.25" X 8.25"',
  '8.5" X 8.5"',
];

// constants.rb ILLUSTRATION_INTERIOR_TYPES
export const ILLUSTRATION_INTERIOR_TYPES = ["Full Color with Bleed", "Full Color"];

// constants.rb ILLUSTRATION_TYPES (authoritative for illustration_type)
export const ILLUSTRATION_TYPES = [
  "Single Page",
  "Double Page (counts as two illustrations)",
  "Mixed",
];

// ---------------------------------------------------------------------------
// Cover design — constants.rb
// ---------------------------------------------------------------------------

// constants.rb INTERIOR_TYPES (cover interior_type)
export const INTERIOR_TYPES = [
  "Black and White",
  "Black and White with Bleed",
  "Full Color",
  "Full Color with Bleed",
];

// constants.rb TRIM_SIZES (15) — cover trim_size + formatting
export const TRIM_SIZES = [
  "5x8",
  "5.06x7.81",
  "5.25x8",
  "5.50x8.50",
  "6x9",
  "6.14x9.21",
  "6.69x9.61",
  "7x10",
  "7.44x9.69",
  "7.50x9.25",
  "8x10",
  "8.50x11",
  "8.25x6",
  "8.25x8.25",
  "8.50x8.50",
];

// constants.rb PAGE_COLORS
export const PAGE_COLORS = ["White", "Cream"];

// constants.rb COVER_PACKAGES (package_type) + COVER_DESIGN_COST starting prices
// + COVER_PACKAGE_DESCRIPTIONS. `value` is submitted.
export const COVER_PACKAGES = [
  {
    value: "Silver",
    label: "Silver",
    startingPrice: 129.99,
    description:
      "Includes one design concept and up to 3 revisions. Optional add-on for source files.",
  },
  {
    value: "Gold",
    label: "Gold",
    startingPrice: 164.99,
    description:
      "Includes two design concepts and up to 4 revisions on the chosen concept. Optional add-on for source files.",
  },
  {
    value: "Platinum",
    label: "Platinum",
    startingPrice: 249.99,
    description:
      "Includes three design concepts and unlimited revisions on the chosen concept. Source files included.",
  },
];

// constants.rb FRONT_COVER_OPTIONS (front_cover_options checkgroup)
export const COVER_FRONT_OPTIONS = ["Title", "Subtitle", "Author's name"];

// constants.rb BACK_COVER_OPTIONS (back_cover_options) — exact casing
export const COVER_BACK_OPTIONS = ["Description/Blurb", "Author Bio", "Author Photo"];

// constants.rb SPINE_COVER_OPTIONS (spine_cover_options)
export const COVER_SPINE_OPTIONS = ["Title", "Author's name", "Logo"];

// constants.rb COLOR_PALETTES (11) — submit the KEY (cover/illustration
// color_palettes[]), label is display only.
export const COLOR_PALETTES = [
  { key: "aquas", label: "Aquas" },
  { key: "blues", label: "Blues" },
  { key: "dark_neutrals", label: "Dark Neutrals" },
  { key: "greens", label: "Greens" },
  { key: "light_neutrals", label: "Light Neutrals" },
  { key: "oranges", label: "Oranges" },
  { key: "pinks", label: "Pinks" },
  { key: "purples", label: "Purples" },
  { key: "reds", label: "Reds" },
  { key: "yellows", label: "Yellows" },
  { key: "designer_suggested", label: "Designer Suggested" },
];

// Style sliders (cover + illustration) — 8 axes, each a 1..5 integer attr.
// Keys from the whitelist; left/right labels from cover_design/_six.erb.
export const STYLE_SLIDER_AXES = [
  { key: "simple_or_detailed", label: "Simplicity", leftLabel: "Simple", rightLabel: "Detailed" },
  { key: "contemporary_or_retro", label: "Era", leftLabel: "Contemporary", rightLabel: "Retro" },
  { key: "formal_or_informal", label: "Formality", leftLabel: "Formal", rightLabel: "Informal" },
  { key: "whimsical_or_serious", label: "Mood", leftLabel: "Whimsical", rightLabel: "Serious" },
  { key: "loud_or_subdued", label: "Volume", leftLabel: "Loud", rightLabel: "Subdued" },
  { key: "feminine_or_masculine", label: "Tone", leftLabel: "Feminine", rightLabel: "Masculine" },
  { key: "soft_or_edgy", label: "Edge", leftLabel: "Soft", rightLabel: "Edgy" },
  { key: "literal_or_abstract", label: "Concept", leftLabel: "Literal", rightLabel: "Abstract" },
];

// ---------------------------------------------------------------------------
// Editing / enhance-text — constants.rb
// ---------------------------------------------------------------------------

// constants.rb EDITING_STYLES (editing editing_style)
export const EDITING_STYLES = [
  "Essential Editing",
  "Line Editing",
  "Developmental Editing",
];

// constants.rb WRITING_EDITING_STYLES (editing_style on seo/book_description/
// humanize_ai/book_writing — the "included copyedit upgrade" select)
export const WRITING_EDITING_STYLES = [
  "Essential Editing",
  "Line & Essential Editing",
];

// formatting_posting.rb enum :formatting_type — submit the snake_case KEY
// (paperback_formatting / ebook_formatting), NOT the display "Paperback"/"eBook".
export const FORMATTING_TYPES = [
  { value: "paperback_formatting", label: "Paperback" },
  { value: "ebook_formatting", label: "eBook" },
];

// constants.rb OUTLINE_PACKAGES (outline package_type) + PACKAGE_OUTLINE_TYPE_COST.
// Plot Outline price = PLOT_OUTLINE_COST(120) + OUTLINE_COST(300) = 420.
export const OUTLINE_PACKAGES = [
  { value: "Professional Outline", label: "Professional Outline", price: 300 },
  { value: "Plot Outline", label: "Plot Outline", price: 420 },
];

// constants.rb TRANSLATION_LANGUAGES (content_translation language) +
// TRANSLATOR_COST rate per 100 words
export const TRANSLATION_LANGUAGES = [
  { value: "German", label: "German", rate: 3.55 },
  { value: "Spanish", label: "Spanish", rate: 3.19 },
];

// constants.rb TRANSLATION_CATEGORIES (content_translation category)
export const TRANSLATION_CATEGORIES = [
  "Fiction",
  "Non-fiction",
  "Blog/Article",
  "Web Content/SEO",
];

// constants.rb PLAGIARISM_REFERENCE_STYLES (plagiarism reference_style) — order
// mirrors the constant (MLA APA Chicago Other); membership only, order not validated.
export const REFERENCE_STYLES = ["MLA", "APA", "Chicago", "Other"];

// ---------------------------------------------------------------------------
// Narration — narration_posting.rb enums (submit the enum KEY)
// ---------------------------------------------------------------------------
export const NARRATION_ACCENTS = [
  { value: "open_to_all", label: "Open to all" },
  { value: "american", label: "American" },
  { value: "british", label: "British" },
];

// Design labels (service-config.js GENDERS): "Male or Female" capitalized. The
// submitted value is the enum key (male_or_female / male / female); only the
// display label is design-owned.
export const NARRATION_GENDERS = [
  { value: "male_or_female", label: "Male or Female" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

// editing_posting english_type — editing/_two.html.erb select pairs (submit US/UK)
export const ENGLISH_TYPES = [
  { value: "US", label: "American English" },
  { value: "UK", label: "UK English" },
];

// ---------------------------------------------------------------------------
// Trigger warnings — _trigger_warning_fields.html.erb (8 categories, exact).
// Serialized into trigger_warning_details JSON (freeform), not enum-validated.
// ---------------------------------------------------------------------------
export const TRIGGER_CATEGORIES = [
  "Sexual Assault/Abuse",
  "Domestic Violence",
  "Prejudice/Discrimination",
  "Abortion",
  "Alcoholism/Drug Abuse",
  "Child Abuse",
  "Sexually Explicit",
  "Animal Abuse",
];

// ---------------------------------------------------------------------------
// AI consent — _ai_consent.html.erb. The 7 bullets shown under the "all" option.
// ---------------------------------------------------------------------------
export const AI_CONSENT_ALL_BULLETS = [
  "Adjust tone, phrasing, and style on their own written content",
  "Generate content which they will modify further to ensure depth and originality",
  "Reduce wordiness and repetition and cut down the fluff",
  "Structuring, layout, and planning chapters and the project as a whole",
  "Enrich content and expand on ideas",
  "Basic grammar and spelling checks",
  "Keyword research if applicable",
];

// AI consent option labels (shared) — design service-config.js AI_CONSENT
// (L54-57). The design's full-sentence labels replace the build's invented
// short labels ("No AI use" etc.), keyed by the submitted carrier value so the
// FieldRenderer consent case and the review row read the same source
// (Plumbline Phase 2, WI-A6). Backend-aligned: the policy scope matches
// _ai_consent.html.erb / _design_ai_consent.html.erb.
export const AI_CONSENT_LABELS = {
  none: "Freelancers may not use AI at all",
  research: "Freelancers may use AI for research & brainstorming",
  all: "Freelancers may use AI more broadly (they edit & own the output)",
};

// Design AI consent — _design_ai_consent.html.erb. The 7 bullets shown under the
// "all" option for cover design + illustration (the 2-option consent). Distinct
// from the writing scope above: design consent covers image/design AI use, not
// prose generation (Plumbline bug 2).
export const DESIGN_AI_CONSENT_ALL_BULLETS = [
  "Edit, refine and improve original designs and illustrations",
  "Generate images which they will modify further to ensure originality",
  "Manipulate designs using AI functions such as generative fill, automatic object removal, color replacement and correction, and Style Transfer Effects",
  "Generate text and text effects",
  "Create templates, layouts and storyboards",
  "Use AI generated images as reference",
  "Analyze design trends and user preferences to create illustrations/designs that resonate with the target audience",
];

// ---------------------------------------------------------------------------
// Managed services — job_posting.rb enum :managed_services_tier =
// ManagedService.purchasable_tiers.merge(self_managed: 0). The only tiers are
// "self_managed" and "managed" (the retired supported/guided products would
// raise on assignment). The managed copy carries the mandatory-QA/QC-fee
// disclosure, the update cadence, the completion email, and the two optional
// Zoom calls the backend policy states.
export const MANAGED_TIERS = [
  {
    value: "self_managed",
    label: "No Management",
    desc: "I will manage my own order without assistance and stay in full control of freelancer selection, chat, and approvals.",
  },
  {
    value: "managed",
    label: "Managed",
    desc: "Fully hands-off. Provide all details upfront and your Project Manager handles the rest. Includes mandatory Quality Assurance and Quality Control (an additional fee based on word count) so nothing ships unreviewed. You choose how often you are updated, and on completion you receive an email with links to download every item and all project documentation. Includes up to two optional Zoom calls.",
  },
];

// Managed-services questionnaire (managed_service_details) — copy from
// config/locales/en.yml (job_postings.steps.managed_services.questions +
// models.managed_service). Shown only when tier != self_managed (i.e. managed).
// freelancer_selection / progress_updates / zoom_call submit string keys;
// pre_scheduling / additional_services submit booleans. The managed tier's
// zoom_call options are {0,3,4} (helper managed_zoom_options).
export const QUESTIONNAIRE_OPTIONS = {
  freelancer_selection: [
    { value: "1", label: "Pick for me" },
    { value: "2", label: "Provide me a shortlist" },
  ],
  pre_scheduling: [
    { value: true, label: "I am willing to wait for the best fit for my project" },
    {
      value: false,
      label: "Please have the most qualified available freelancer start my project as soon as possible",
    },
  ],
  progress_updates: [
    { value: "1", label: "Only regarding delays or major changes" },
    { value: "2", label: "Once per project item" },
    { value: "3", label: "Biweekly" },
    { value: "4", label: "Once per week" },
    { value: "5", label: "Every 2-3 days" },
  ],
  zoom_call_managed: [
    { value: "0", label: "No calls, email only" },
    { value: "3", label: "One 60-minute call" },
    { value: "4", label: "Two 30-minute calls" },
  ],
  additional_services: [
    { value: true, label: "Notify me of additional service options" },
    { value: false, label: "Do not notify me of potential upgrades to my project" },
  ],
};

// ---------------------------------------------------------------------------
// RATES — numeric constants for the client-side estimate (PriceRail). The
// /post/price server endpoint (PostingCalculator) is authoritative; these are
// the "current" PriceList values from constants.rb (NOT the OLD_* tables).
// Units noted per entry. Match Ruby exact-float (no Math.ceil) except formatting.
// ---------------------------------------------------------------------------
export const RATES = {
  // Book writing — constants.rb WRITER_COST (per 100 words)
  WRITER_COST: {
    Premium: 4.49,
    Top: 4.29,
    Urban: 3.89,
    Rising: 3.3,
    "Finance and Technology": 5.5,
  },
  // constants.rb AI_WRITER_COST (use_ai book_writing, per 100 words)
  AI_WRITER_COST: {
    Premium: 2.33,
    Top: 2.17,
    Urban: 1.97,
    Rising: 1.7,
    "Finance and Technology": 5.5,
  },
  // constants.rb SAMPLE_WRITER_COST (per 100 words; sample is fixed 1000 words)
  SAMPLE_WRITER_COST: {
    Premium: 3.2,
    Top: 2.8,
    Urban: 2.6,
    Rising: 2.2,
  },
  // constants.rb HUMANIZE_AI_WRITER_COST (humanize_ai, human path, per 100 words)
  HUMANIZE_AI_WRITER_COST: {
    Premium: 4.5,
    Top: 4.26,
    Urban: 3.84,
    Rising: 3.3,
  },
  // constants.rb HUMANIZE_AI_WITH_AI_WRITER_COST (humanize_ai, use_ai, per 100 words)
  HUMANIZE_AI_WITH_AI_WRITER_COST: {
    Premium: 3.0,
    Top: 2.7,
    Urban: 2.34,
    Rising: 2.22,
  },
  // constants.rb NARRATOR_COST (per 100 words; Ruby uses floor(words/100)*rate)
  NARRATOR_COST: 1.95,
  // constants.rb OUTLINE_COST / PLOT_OUTLINE_COST (flat). Plot total = 300 + 120.
  OUTLINE_COST: 300,
  PLOT_OUTLINE_COST: 120,
  PLOT_OUTLINE_TOTAL: 420,
  // constants.rb ILLUSTRATION_COST (per illustration) + ILLUSTRATION_ADD_ON_COST
  ILLUSTRATION_COST: 45,
  ILLUSTRATION_ADD_ON_COST: 75,
  // constants.rb HUMANIZE_AI_IMAGE_COST (per image add-on)
  HUMANIZE_AI_IMAGE_COST: 6,
  // constants.rb DESCRIPTION_COST (book_description, flat) + word count basis
  DESCRIPTION_COST: 94.99,
  BOOK_DESCRIPTION_WORD_COUNT: 500,
  // constants.rb COVER_DESIGN_COST (by package_type) + COVER_ADD_ON_COST.
  // Platinum source-files add-on is $0 (included) per CORRECTIONS.
  COVER_DESIGN_COST: {
    Silver: 129.99,
    Gold: 164.99,
    Platinum: 249.99,
  },
  COVER_ADD_ON_COST: 90,
  // constants.rb EDITING_COST (editing editing_style, per 100 words; ×10 = /1000)
  EDITING_COST: {
    "Essential Editing": 2.1,
    "Line Editing": 2.4,
    "Developmental Editing": 2.8,
  },
  // constants.rb WRITING_EDITING_STYLES_COST (editing_style upgrade, per 100 words).
  // Book Description line-editing add = (500/100)*0.8 = 4.00.
  WRITING_EDITING_STYLES_COST: {
    "Essential Editing": 0,
    "Line & Essential Editing": 0.8,
  },
  // constants.rb FORMATTING_TYPES_COST — per 10,000 words (Ruby ceils per 10k
  // bucket). Keyed here by the submitted enum value for client lookup.
  FORMATTING_TYPES_COST: {
    paperback_formatting: 25.0,
    ebook_formatting: 18.0,
  },
  // constants.rb SEO_COST — per 100 words, bucketed by article_count threshold
  // (key = articles remaining bucket "30".."0"). Per-word rate stepping.
  SEO_COST: {
    30: 6.39,
    20: 6.43,
    15: 6.47,
    10: 6.51,
    5: 6.55,
    0: 6.59,
  },
  // constants.rb TRANSLATOR_COST (content_translation, per 100 words)
  TRANSLATOR_COST: {
    German: 3.55,
    Spanish: 3.19,
  },
  // constants.rb PLAGIARISM_COST (per 100 words) + PLAGIARISM_SCAN_COST
  PLAGIARISM_COST: 0.9,
  PLAGIARISM_SCAN_COST: 0.6,
  // constants.rb ADDON_COST — QA: $225 base (<=50k words) then +$2.25/1000 beyond.
  // QC: $10/1000 words. Match Ruby exact-float.
  ADDON_COST: {
    qa_base: 225.0,
    qa_per_thousand: 2.25,
    quality_control: 10.0,
  },
  // constants.rb sample/bundle word-count anchors
  SAMPLE_WORD_COUNT: 1000,
  BUNDLE_MIN_WORD_COUNT: 10000,
  // constants.rb BUNDLE_DISCOUNTS (flat $ off the bundle, by bundle_tier). Used by
  // the client bundle estimate; the server /post/price total is authoritative.
  BUNDLE_DISCOUNTS: {
    Silver: 20,
    Gold: 30,
    Platinum: 40,
    "Ultimate Fiction": 50,
    "Audiobook Narration": 50,
  },
};

// SEO per-article rate bucket (per 100 words). Mirrors seo_posting.rb#cost_per_article
// (lines 152-167): article_count >= 30 → '30', >= 20 → '20', >= 15 → '15',
// >= 10 → '10', >= 5 → '5', else → '0'. Driven off RATES.SEO_COST so the
// figures stay single-sourced. Returns the bucket rate (e.g. 6.39).
export function SEO_RATE_FOR(articleCount) {
  const count = Number(articleCount) || 0;

  let bucket;
  if (count >= 30) bucket = 30;
  else if (count >= 20) bucket = 20;
  else if (count >= 15) bucket = 15;
  else if (count >= 10) bucket = 10;
  else if (count >= 5) bucket = 5;
  else bucket = 0;

  return RATES.SEO_COST[bucket];
}
